import {
  LayoutDashboard, Building2, Users, Radio, Calendar, FileText,
  Settings, CreditCard, Zap, LogOut, Plug, LifeBuoy,
  Megaphone, ClipboardList, Wallet, UserCog,
  ChevronRight, Brain, Briefcase, Workflow, TrendingUp,
  MessagesSquare, Bell, Activity, Upload, Heart,
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAgency } from '@/contexts/AgencyContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FlowTokLogo } from '@/components/FlowTokLogo';
import { AgencyLogo } from '@/components/AgencyLogo';
import { useState, useEffect, useMemo } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';

type LeafItem = { title: string; url: string; icon: any; roles?: readonly string[] };
type NavNode =
  | LeafItem
  | { title: string; icon: any; key: string; children: LeafItem[] };

/**
 * FlowTokOS sidebar — organized by BUSINESS FUNCTION (operating systems),
 * not by feature/table. Five OS pillars + Settings.
 *
 *   AGENCY OS    — executive + business operations
 *   SELLER OS    — brand + commerce management
 *   AFFILIATE OS — creator ecosystem
 *   LIVE OS      — livestream commerce intelligence
 *   SETTINGS     — administrative + configuration (Employees lives here)
 *
 * Rules:
 *   - Each OS is a collapsible section.
 *   - Nested workflows (e.g. CRM → Pipeline, Sessions → Schedule) collapse
 *     under their parent rather than being top-level destinations.
 *   - Alerts is intentionally NOT a sidebar destination — surfaced via the
 *     header bell + contextual warnings + activity feed instead.
 *   - Employees is administrative, not operational — lives under Settings.
 */
const navTree: {
  label: string;
  roles: readonly string[];
  items: NavNode[];
}[] = [
  {
    label: 'AGENCY',
    roles: ['super_admin', 'agency_staff'],
    items: [
      { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
      { title: 'Brands', url: '/brands', icon: Building2 },
      {
        title: 'Creators',
        icon: Users,
        key: 'creators',
        children: [
          { title: 'Creator Roster', url: '/creators', icon: Users },
          { title: 'Community', url: '/community', icon: Heart },
          { title: 'Creator Contracts', url: '/contracts', icon: FileText },
          { title: 'Creator Payouts', url: '/creator-payouts', icon: Wallet },
        ],
      },
      { title: 'CRM', url: '/crm', icon: Briefcase },
      { title: 'Inbox', url: '/inbox', icon: MessagesSquare },
      { title: 'Account', url: '/settings', icon: Settings },
    ],
  },
];


const COLLAPSE_STORAGE_KEY = 'sidebar:collapsed-groups:v1';

function useGroupState(key: string, defaultOpen: boolean) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      if (!raw) return defaultOpen;
      const state = JSON.parse(raw) as Record<string, boolean>;
      return key in state ? state[key] : defaultOpen;
    } catch { return defaultOpen; }
  });
  useEffect(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      const state = raw ? JSON.parse(raw) : {};
      state[key] = open;
      localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(state));
    } catch { /* noop */ }
  }, [key, open]);
  return [open, setOpen] as const;
}

function LeafLink({ item, collapsed, depth = 0 }: { item: LeafItem; collapsed: boolean; depth?: number }) {
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild>
        <NavLink
          to={item.url}
          end={item.url === '/dashboard'}
          onClick={() => { if (isMobile) setOpenMobile(false); }}
          className={`group relative rounded-md ${depth > 0 ? 'pl-8' : 'px-2'} py-1.5 text-[12.5px] font-medium text-sidebar-foreground hover:text-foreground hover:bg-sidebar-accent/60 transition-all duration-150`}
          activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-semibold shadow-xs before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[2.5px] before:rounded-full before:bg-primary"
        >
          <span className="flex h-5 w-5 items-center justify-center rounded-md mr-2 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors">
            <item.icon className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
          {!collapsed && <span className="truncate">{item.title}</span>}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function CollapsibleGroup({
  node,
  collapsed,
  currentPath,
}: { node: Extract<NavNode, { children: LeafItem[] }>; collapsed: boolean; currentPath: string }) {
  const hasActive = node.children.some(c => currentPath.startsWith(c.url));
  const [open, setOpen] = useGroupState(`grp:${node.key}`, hasActive);

  useEffect(() => { if (hasActive && !open) setOpen(true); /* eslint-disable-next-line */ }, [hasActive]);

  if (collapsed) {
    // When sidebar is collapsed, render children as flat icons
    return (
      <>
        {node.children.map(c => <LeafLink key={c.title} item={c} collapsed />)}
      </>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <button
            className="group relative w-full flex items-center rounded-md px-2 py-1.5 text-[12.5px] font-medium text-sidebar-foreground hover:text-foreground hover:bg-sidebar-accent/60 transition-all duration-150"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-md mr-2 shrink-0 text-muted-foreground group-hover:text-foreground">
              <node.icon className="h-3.5 w-3.5" strokeWidth={2} />
            </span>
            <span className="truncate flex-1 text-left">{node.title}</span>
            <ChevronRight
              className={`h-3 w-3 text-muted-foreground/60 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
              strokeWidth={2.5}
            />
          </button>
        </CollapsibleTrigger>
      </SidebarMenuItem>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="mt-0.5 space-y-0.5">
          {node.children.map(c => <LeafLink key={c.title} item={c} collapsed={false} depth={1} />)}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function SectionBlock({
  label,
  items,
  collapsed,
  currentPath,
  first,
}: {
  label: string;
  items: NavNode[];
  collapsed: boolean;
  currentPath: string;
  first: boolean;
}) {
  const hasActiveInSection = useMemo(() => {
    return items.some(it => {
      if ('url' in it) return currentPath.startsWith(it.url);
      return it.children.some(c => currentPath.startsWith(c.url));
    });
  }, [items, currentPath]);

  const [open, setOpen] = useGroupState(`sec:${label}`, true);

  useEffect(() => { if (hasActiveInSection && !open) setOpen(true); /* eslint-disable-next-line */ }, [hasActiveInSection]);

  return (
    <SidebarGroup className={first ? 'mb-0' : 'mb-0 mt-3'}>
      {!collapsed && (
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between px-2.5 mb-1 text-[9.5px] font-bold uppercase tracking-[0.13em] text-muted-foreground/55 hover:text-muted-foreground transition-colors">
              <span>{label}</span>
              <ChevronRight className={`h-2.5 w-2.5 transition-transform ${open ? 'rotate-90' : ''}`} strokeWidth={3} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <SidebarGroupContent>
              <SidebarMenu className="space-y-0.5">
                {items.map((it) =>
                  'url' in it
                    ? <LeafLink key={it.title} item={it} collapsed={false} />
                    : <CollapsibleGroup key={it.key} node={it} collapsed={false} currentPath={currentPath} />
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </CollapsibleContent>
        </Collapsible>
      )}
      {collapsed && (
        <SidebarGroupContent>
          <SidebarMenu className="space-y-0.5">
            {items.map((it) =>
              'url' in it
                ? <LeafLink key={it.title} item={it} collapsed />
                : <CollapsibleGroup key={it.key} node={it} collapsed currentPath={currentPath} />
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
}

export function AppSidebar() {
  const { role, user, signOut } = useAuth();
  useAgency();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === 'collapsed';
  const { pathname } = useLocation();

  // Ensure the mobile drawer always closes on route change (e.g. via back button or programmatic navigation).
  useEffect(() => { if (isMobile) setOpenMobile(false); /* eslint-disable-next-line */ }, [pathname]);

  const { agencyId } = useAgency();

  const { data: profile } = useQuery({
    queryKey: ['my-profile-sidebar', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase.from('profiles').select('brand_id, full_name').eq('id', user.id).single();
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: agencyMeta } = useQuery({
    queryKey: ['sidebar-agency', agencyId],
    enabled: !!agencyId,
    queryFn: async () => {
      const { data } = await supabase
        .from('agencies')
        .select('name, logo_url')
        .eq('id', agencyId!)
        .maybeSingle();
      return data;
    },
  });

  const clientBrandSections = role === 'brand_client' && profile?.brand_id ? [
    {
      label: 'MY BRAND',
      roles: ['brand_client'],
      items: [
        { title: 'Dashboard', url: `/brands/${profile.brand_id}`, icon: LayoutDashboard },
      ] as NavNode[],
    },
  ] : [];

  const sections = role === 'brand_client'
    ? clientBrandSections
    : navTree.filter(s => !role || s.roles.includes(role));

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || '?';

  // Bottom utility nav — calm, secondary access
  const utilityItems: LeafItem[] = [
    { title: 'Profile & Workspace', url: '/me', icon: UserCog },
    { title: 'Help Center', url: '/help', icon: LifeBuoy },
  ];

  return (
    <Sidebar collapsible="icon" className="bg-sidebar border-r border-sidebar-border">
      <SidebarHeader className="px-3 py-3">
        <Link to="/" className="hover:opacity-80 transition-opacity duration-150">
          {!collapsed ? (
            <div className="pb-3 border-b border-sidebar-border/70">
              <FlowTokLogo size={26} />
            </div>
          ) : (
            <div className="flex justify-center">
              <FlowTokLogo size={24} showWordmark={false} />
            </div>
          )}
        </Link>
        {agencyMeta?.name && !collapsed && (
          <div className="mt-3 flex items-center gap-2 px-2 py-1.5 rounded-md bg-sidebar-accent/40">
            <AgencyLogo name={agencyMeta.name} logoUrl={agencyMeta.logo_url} size={24} />
            <span className="text-xs font-medium truncate">{agencyMeta.name}</span>
          </div>
        )}
        {agencyMeta?.name && collapsed && (
          <div className="mx-auto mt-2">
            <AgencyLogo name={agencyMeta.name} logoUrl={agencyMeta.logo_url} size={24} />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="px-2 pt-2">
        {sections.map((section, idx) => (
          <SectionBlock
            key={section.label}
            label={section.label}
            items={section.items}
            collapsed={collapsed}
            currentPath={pathname}
            first={idx === 0}
          />
        ))}

        {/* Bottom utility navigation — quiet, secondary */}
        <SidebarGroup className="mt-auto pt-3 border-t border-sidebar-border/60">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-0.5">
              {utilityItems.map(it => <LeafLink key={it.title} item={it} collapsed={collapsed} />)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {!collapsed && (
        <SidebarFooter className="p-2.5 space-y-2 border-t border-sidebar-border/70">
          <div className="flex items-center gap-2.5 rounded-lg p-1.5 hover:bg-sidebar-accent/60 transition-colors duration-150">
            <div
              className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 shadow-sm"
              style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent)))' }}
            >
              <span className="text-[10.5px] font-bold text-white">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] text-foreground truncate font-semibold leading-tight">{profile?.full_name || user?.email}</p>
              <p className="text-[9.5px] text-muted-foreground/80 uppercase tracking-[0.08em] font-semibold">{role?.replace('_', ' ')}</p>
            </div>
            <button
              onClick={signOut}
              className="p-1.5 rounded-md hover:bg-secondary transition-colors duration-150"
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
